import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import {
  getClientCourse,
  createClientCourse,
  archiveClientCourse,
  getClientCommunityOptions,
} from "../../api/clientCoursesApi";
import {
  saveCourseDraft,
  submitCourseForReview,
  listCourseVersions,
} from "../../api/clientPublishingApi";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";
import ClientPortalImageUpload from "../../components/clientPortal/ClientPortalImageUpload";
import ExternalLinksEditor from "../../components/clientPortal/ExternalLinksEditor";
import { resolveClientPortalAssetUrl } from "../../api/clientUploadApi";
import "../../styles/clientCourseForm.css";

const DISPLAY_STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "HIDDEN", label: "Hidden" },
  { value: "ARCHIVED", label: "Archived" },
];

const STATUS_TONE = {
  DRAFT: "neutral",
  PENDING_REVIEW: "warning",
  APPROVED: "info",
  REJECTED: "danger",
  PUBLISHED: "success",
  ARCHIVED: "neutral",
};

const COURSE_TYPES = [
  "",
  "Championship",
  "Executive",
  "Par 3",
  "Resort",
  "Private Club",
  "Public",
  "Semi-Private",
  "Municipal",
  "Other",
];

const PUBLIC_PRIVATE_TYPES = [
  "",
  "Public",
  "Private",
  "Semi-Private",
  "Resort",
  "Municipal",
  "Unknown",
];

const TABS = [
  { key: "details", label: "Details", icon: "\u270F\uFE0F" },
  { key: "course", label: "Course Info", icon: "\u26F3" },
  { key: "location", label: "Location", icon: "\uD83D\uDCCD" },
  { key: "media", label: "Media & Contact", icon: "\uD83D\uDDBC\uFE0F" },
  { key: "facilities", label: "Facilities", icon: "\u2728" },
  { key: "seo", label: "SEO", icon: "\uD83D\uDD0D" },
];

const EMPTY_FORM = {
  courseName: "",
  slug: "",
  communityId: "",
  shortDescription: "",
  longDescription: "",
  courseType: "",
  publicPrivateType: "",
  numberOfHoles: "",
  par: "",
  yardage: "",
  designer: "",
  yearOpened: "",
  displayStatus: "DRAFT",
  isFeatured: false,
  sortOrder: "",
  websiteUrl: "",
  externalLinks: [],
  phone: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  stateRegion: "",
  postalCode: "",
  country: "US",
  latitude: "",
  longitude: "",
  clubhouseInfo: "",
  amenities: "",
  heroImageUrl: "",
  seoTitle: "",
  seoDescription: "",
};

function snapshotToForm(snapshot) {
  if (!snapshot) return null;
  return {
    courseName: snapshot.courseName || "",
    slug: snapshot.slug || "",
    communityId: snapshot.communityId || "",
    shortDescription: snapshot.shortDescription || "",
    longDescription: snapshot.longDescription || "",
    courseType: snapshot.courseType || "",
    publicPrivateType: snapshot.publicPrivateType || "",
    numberOfHoles: snapshot.numberOfHoles ?? "",
    par: snapshot.par ?? "",
    yardage: snapshot.yardage ?? "",
    designer: snapshot.designer || "",
    yearOpened: snapshot.yearOpened ?? "",
    displayStatus: snapshot.displayStatus || "DRAFT",
    isFeatured: Boolean(snapshot.isFeatured),
    sortOrder: snapshot.sortOrder ?? "",
    websiteUrl: snapshot.websiteUrl || "",
    externalLinks: Array.isArray(snapshot.externalLinks) ? snapshot.externalLinks : [],
    phone: snapshot.phone || "",
    email: snapshot.email || "",
    addressLine1: snapshot.addressLine1 || "",
    addressLine2: snapshot.addressLine2 || "",
    city: snapshot.city || "",
    stateRegion: snapshot.stateRegion || "",
    postalCode: snapshot.postalCode || "",
    country: snapshot.country || "US",
    latitude: snapshot.latitude ?? "",
    longitude: snapshot.longitude ?? "",
    clubhouseInfo: snapshot.clubhouseInfo || "",
    amenities: Array.isArray(snapshot.amenities) ? snapshot.amenities.join(", ") : "",
    heroImageUrl: snapshot.heroImageUrl || "",
    seoTitle: snapshot.seoTitle || "",
    seoDescription: snapshot.seoDescription || "",
  };
}

function courseToForm(course) {
  if (course.draftSnapshot) {
    const fromDraft = snapshotToForm(course.draftSnapshot);
    if (fromDraft) return fromDraft;
  }
  return {
    courseName: course.courseName || "",
    slug: course.slug || "",
    communityId: course.communityId || "",
    shortDescription: course.shortDescription || "",
    longDescription: course.longDescription || "",
    courseType: course.courseType || "",
    publicPrivateType: course.publicPrivateType || "",
    numberOfHoles: course.numberOfHoles ?? "",
    par: course.par ?? "",
    yardage: course.yardage ?? "",
    designer: course.designer || "",
    yearOpened: course.yearOpened ?? "",
    displayStatus: course.displayStatus || "DRAFT",
    isFeatured: Boolean(course.isFeatured),
    sortOrder: course.sortOrder ?? "",
    websiteUrl: course.websiteUrl || "",
    externalLinks: Array.isArray(course.externalLinks) ? course.externalLinks : [],
    phone: course.phone || "",
    email: course.email || "",
    addressLine1: course.addressLine1 || "",
    addressLine2: course.addressLine2 || "",
    city: course.city || "",
    stateRegion: course.stateRegion || "",
    postalCode: course.postalCode || "",
    country: course.country || "US",
    latitude: course.latitude ?? "",
    longitude: course.longitude ?? "",
    clubhouseInfo: course.clubhouseInfo || "",
    amenities: Array.isArray(course.amenities) ? course.amenities.join(", ") : "",
    heroImageUrl: course.heroImageUrl || "",
    seoTitle: course.seoTitle || "",
    seoDescription: course.seoDescription || "",
  };
}

function FormField({ label, required, hint, children }) {
  return (
    <label className="ccf-field">
      <span className="ccf-field__label">
        {label}
        {required && <span className="ccf-field__req">*</span>}
      </span>
      {children}
      {hint && <span className="ccf-field__hint">{hint}</span>}
    </label>
  );
}

function CharCount({ value, max }) {
  const len = (value || "").length;
  return (
    <span className={`ccf-field__charcount${len > max * 0.9 ? " ccf-field__charcount--warn" : ""}`}>
      {len}/{max}
    </span>
  );
}

function TagPills({ value }) {
  if (!value) return null;
  const tags = value.split(",").map((t) => t.trim()).filter(Boolean);
  if (!tags.length) return null;
  return (
    <div className="ccf-tags">
      {tags.map((tag) => (
        <span key={tag} className="ccf-tag">{tag}</span>
      ))}
    </div>
  );
}

function DetailsSection({ form, updateField, communities }) {
  return (
    <div className="cof-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Basic Information</h2>
        <p className="ccf-card__description">The name and descriptions shown to visitors</p>
        <div className="ccf-card__body">
          <div className="ccf-row ccf-row--2">
            <FormField label="Course name" required>
              <input value={form.courseName} onChange={updateField("courseName")} required className="ccf-input" placeholder="e.g. Bear's Best Las Vegas" />
            </FormField>
            <FormField label="URL slug" hint="Leave blank to auto-generate from name">
              <input value={form.slug} onChange={updateField("slug")} placeholder="auto-from-name" className="ccf-input" />
            </FormField>
          </div>
          <FormField label="Associated community" hint="Link this course to a community">
            <select value={form.communityId} onChange={updateField("communityId")} className="ccf-input ccf-input--select">
              <option value="">No community</option>
              {communities.map((c) => (
                <option key={c.communityId} value={c.communityId}>
                  {c.communityName}
                  {[c.city, c.stateRegion].filter(Boolean).length
                    ? ` \u2014 ${[c.city, c.stateRegion].filter(Boolean).join(", ")}`
                    : ""}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Short description" hint="Shown in cards and search results">
            <textarea value={form.shortDescription} onChange={updateField("shortDescription")} rows={2} className="ccf-input ccf-input--textarea" placeholder="A brief overview of the course..." />
          </FormField>
          <FormField label="Full description" hint="Detailed content for the course page">
            <textarea value={form.longDescription} onChange={updateField("longDescription")} rows={6} className="ccf-input ccf-input--textarea" placeholder="Describe the course, its history, layout, signature holes, and what makes it unique..." />
          </FormField>
        </div>
      </section>
    </div>
  );
}

function CourseInfoSection({ form, updateField }) {
  return (
    <div className="cof-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Course Details</h2>
        <p className="ccf-card__description">Classification and specifications</p>
        <div className="ccf-card__body">
          <div className="ccf-row ccf-row--2">
            <FormField label="Course type">
              <select value={form.courseType} onChange={updateField("courseType")} className="ccf-input ccf-input--select">
                {COURSE_TYPES.map((t) => (
                  <option key={t || "none"} value={t}>{t || "Select type"}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Public / Private">
              <select value={form.publicPrivateType} onChange={updateField("publicPrivateType")} className="ccf-input ccf-input--select">
                {PUBLIC_PRIVATE_TYPES.map((t) => (
                  <option key={t || "none"} value={t}>{t || "Select type"}</option>
                ))}
              </select>
            </FormField>
          </div>
          <div className="ccf-row ccf-row--2">
            <FormField label="Number of holes">
              <input type="number" min="1" value={form.numberOfHoles} onChange={updateField("numberOfHoles")} className="ccf-input" placeholder="18" />
            </FormField>
            <FormField label="Par">
              <input type="number" min="1" value={form.par} onChange={updateField("par")} className="ccf-input" placeholder="72" />
            </FormField>
          </div>
          <div className="ccf-row ccf-row--2">
            <FormField label="Yardage" hint="Total yardage from the tips">
              <input type="number" min="1" value={form.yardage} onChange={updateField("yardage")} className="ccf-input" placeholder="7200" />
            </FormField>
            <FormField label="Year opened">
              <input type="number" value={form.yearOpened} onChange={updateField("yearOpened")} className="ccf-input" placeholder="2001" />
            </FormField>
          </div>
          <FormField label="Designer / Architect" hint="Course designer or architecture firm">
            <input value={form.designer} onChange={updateField("designer")} className="ccf-input" placeholder="e.g. Jack Nicklaus" />
          </FormField>
        </div>
      </section>
    </div>
  );
}

function LocationSection({ form, updateField }) {
  return (
    <div className="cof-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Address</h2>
        <p className="ccf-card__description">Street address for this course</p>
        <div className="ccf-card__body">
          <FormField label="Address line 1">
            <input value={form.addressLine1} onChange={updateField("addressLine1")} className="ccf-input" placeholder="123 Golf Club Drive" />
          </FormField>
          <FormField label="Address line 2" hint="Suite, unit, building, etc.">
            <input value={form.addressLine2} onChange={updateField("addressLine2")} className="ccf-input" placeholder="Suite 100" />
          </FormField>
          <div className="ccf-row ccf-row--2">
            <FormField label="City">
              <input value={form.city} onChange={updateField("city")} className="ccf-input" placeholder="Las Vegas" />
            </FormField>
            <FormField label="State / Region">
              <input value={form.stateRegion} onChange={updateField("stateRegion")} className="ccf-input" placeholder="Nevada" />
            </FormField>
          </div>
          <div className="ccf-row ccf-row--3">
            <FormField label="Postal code">
              <input value={form.postalCode} onChange={updateField("postalCode")} className="ccf-input" placeholder="89141" />
            </FormField>
            <FormField label="Country">
              <input value={form.country} onChange={updateField("country")} className="ccf-input" placeholder="US" />
            </FormField>
          </div>
        </div>
      </section>

      <section className="ccf-card" style={{ marginTop: 20 }}>
        <h2 className="ccf-card__title">Coordinates</h2>
        <p className="ccf-card__description">Map position for this course</p>
        <div className="ccf-card__body">
          <div className="ccf-row ccf-row--2">
            <FormField label="Latitude" hint="Decimal degrees, e.g. 36.1699">
              <input value={form.latitude} onChange={updateField("latitude")} className="ccf-input" placeholder="36.1699" />
            </FormField>
            <FormField label="Longitude" hint="Decimal degrees, e.g. -115.1398">
              <input value={form.longitude} onChange={updateField("longitude")} className="ccf-input" placeholder="-115.1398" />
            </FormField>
          </div>
          {(form.latitude && form.longitude) && (
            <div className="ccf-map-hint">
              <span className="ccf-map-hint__icon" aria-hidden="true">&#128506;</span>
              Coordinates set: {form.latitude}, {form.longitude}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function MediaContactSection({ clientViewId, courseId, form, setForm, updateField }) {
  return (
    <div className="cof-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Media</h2>
        <p className="ccf-card__description">Hero image for the course page</p>
        <div className="ccf-card__body">
          <ClientPortalImageUpload
            clientViewId={clientViewId}
            kind="course_hero"
            entityId={courseId}
            label="Hero image"
            hint="Recommended size: 1200 × 600px. JPG, PNG, or WebP up to 8MB."
            value={form.heroImageUrl}
            onChange={(heroImageUrl) => setForm((prev) => ({ ...prev, heroImageUrl }))}
          />
          {form.heroImageUrl ? (
            <div className="ccf-image-preview">
              <img
                src={resolveClientPortalAssetUrl(form.heroImageUrl)}
                alt="Hero preview"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          ) : null}
        </div>
      </section>

      <section className="ccf-card" style={{ marginTop: 20 }}>
        <h2 className="ccf-card__title">Contact & Links</h2>
        <p className="ccf-card__description">Website, contact details, and related links</p>
        <div className="ccf-card__body">
          <FormField label="Primary website" hint="The course's official website">
            <input value={form.websiteUrl} onChange={updateField("websiteUrl")} type="url" className="ccf-input" placeholder="https://example.com" />
          </FormField>
          <ExternalLinksEditor
            links={form.externalLinks}
            onChange={(externalLinks) => setForm((prev) => ({ ...prev, externalLinks }))}
          />
          <div className="ccf-row ccf-row--2">
            <FormField label="Phone">
              <input value={form.phone} onChange={updateField("phone")} className="ccf-input" placeholder="(702) 555-0123" />
            </FormField>
            <FormField label="Email">
              <input value={form.email} onChange={updateField("email")} type="email" className="ccf-input" placeholder="info@golfcourse.com" />
            </FormField>
          </div>
        </div>
      </section>
    </div>
  );
}

function FacilitiesSection({ form, updateField }) {
  return (
    <div className="cof-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Facilities & Amenities</h2>
        <p className="ccf-card__description">Clubhouse details and amenity tags</p>
        <div className="ccf-card__body">
          <FormField label="Clubhouse information" hint="Describe dining, pro shop, locker rooms, event spaces, etc.">
            <textarea value={form.clubhouseInfo} onChange={updateField("clubhouseInfo")} rows={4} className="ccf-input ccf-input--textarea" placeholder="Full-service clubhouse with restaurant, pro shop, and event space..." />
          </FormField>
          <FormField label="Amenities" hint="Separate with commas">
            <input value={form.amenities} onChange={updateField("amenities")} className="ccf-input" placeholder="Driving Range, Putting Green, Pro Shop, Restaurant..." />
          </FormField>
          <TagPills value={form.amenities} />
        </div>
      </section>
    </div>
  );
}

function SeoSection({ form, updateField }) {
  return (
    <div className="cof-section-enter">
      <section className="ccf-card">
        <h2 className="ccf-card__title">Search Engine Optimization</h2>
        <p className="ccf-card__description">Control how this course appears in search results</p>
        <div className="ccf-card__body">
          <FormField label="SEO title" hint="Appears as the browser tab title and in search results">
            <input value={form.seoTitle} onChange={updateField("seoTitle")} maxLength={120} className="ccf-input" placeholder="e.g. Bear's Best Las Vegas | Golf Homes" />
            <CharCount value={form.seoTitle} max={120} />
          </FormField>
          <FormField label="SEO description" hint="The meta description shown in search engines">
            <textarea value={form.seoDescription} onChange={updateField("seoDescription")} rows={3} maxLength={320} className="ccf-input ccf-input--textarea" placeholder="Describe the course for search engines..." />
            <CharCount value={form.seoDescription} max={320} />
          </FormField>
        </div>
      </section>
    </div>
  );
}

const ClientCourseFormPage = () => {
  const { clientViewId, courseId } = useParams();
  const isNew = !courseId;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [communities, setCommunities] = useState([]);
  const [canManage, setCanManage] = useState(isNew);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [publishing, setPublishing] = useState(null);
  const [versions, setVersions] = useState([]);
  const [activeTab, setActiveTab] = useState("details");
  const paths = useClientPortalPaths();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getClientCommunityOptions(clientViewId).catch(() => ({ communities: [] })),
      isNew ? Promise.resolve(null) : getClientCourse(clientViewId, courseId),
    ])
      .then(async ([optionsBody, courseBody]) => {
        if (cancelled) return;
        setCommunities(optionsBody.communities || []);
        if (courseBody) {
          setForm(courseToForm(courseBody.course));
          setCanManage(courseBody.canManage);
          setPublishing(courseBody.course.publishing || null);
          try {
            const versionsBody = await listCourseVersions(clientViewId, courseId);
            setVersions(versionsBody.versions || []);
          } catch {
            setVersions([]);
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error.message || "Failed to load course.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId, courseId, isNew]);

  const updateField = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    ...form,
    communityId: form.communityId || null,
    isFeatured: form.isFeatured,
    sortOrder: form.sortOrder === "" ? null : Number(form.sortOrder),
    numberOfHoles: form.numberOfHoles === "" ? null : Number(form.numberOfHoles),
    par: form.par === "" ? null : Number(form.par),
    yardage: form.yardage === "" ? null : Number(form.yardage),
    yearOpened: form.yearOpened === "" ? null : Number(form.yearOpened),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isNew) return handleSaveDraft();
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const result = await createClientCourse(clientViewId, buildPayload());
      setSuccessMessage("Course created.");
      navigate(paths.viewSection(clientViewId, "courses", result.course.courseId, "edit"), {
        replace: true,
      });
    } catch (error) {
      setErrorMessage(error.message || "Failed to save course.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const result = await saveCourseDraft(clientViewId, courseId, buildPayload());
      setPublishing(result.publishing || null);
      setSuccessMessage("Draft saved. Changes are not public until published.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!window.confirm("Submit this course for internal review?")) return;
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await saveCourseDraft(clientViewId, courseId, buildPayload());
      const result = await submitCourseForReview(clientViewId, courseId);
      setPublishing(result.publishing || null);
      setSuccessMessage("Submitted for review.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to submit for review.");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm("Archive this course? This removes it from public view.")) return;
    setSaving(true);
    try {
      await archiveClientCourse(clientViewId, courseId);
      navigate(paths.viewSection(clientViewId, "courses"));
    } catch (error) {
      setErrorMessage(error.message || "Failed to archive course.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ClientPortalLoading message="Loading course..." />;

  if (!canManage && !isNew) {
    return <ClientPortalError message="You have read-only access to courses." />;
  }

  const canSubmitReview =
    publishing &&
    canManage &&
    !publishing.hasPendingReview &&
    (publishing.hasDraftChanges || publishing.publishStatus === "DRAFT" || publishing.publishStatus === "REJECTED");

  const publishHint =
    publishing?.publishStatus === "REJECTED"
      ? "Update your draft and submit again for review."
      : publishing?.publishStatus === "PENDING_REVIEW"
        ? "Your submission is awaiting internal review."
        : publishing?.publishStatus === "APPROVED"
          ? "Approved \u2014 waiting for an admin to publish."
          : publishing?.publishStatus === "PUBLISHED" && publishing?.hasDraftChanges
            ? "You have unpublished draft changes."
            : publishing?.publishStatus === "DRAFT" || publishing?.hasDraftChanges
              ? "Save a draft, then submit for review when ready."
              : null;

  const tabContent = {
    details: <DetailsSection form={form} updateField={updateField} communities={communities} />,
    course: <CourseInfoSection form={form} updateField={updateField} />,
    location: <LocationSection form={form} updateField={updateField} />,
    media: (
      <MediaContactSection
        clientViewId={clientViewId}
        courseId={courseId}
        form={form}
        setForm={setForm}
        updateField={updateField}
      />
    ),
    facilities: <FacilitiesSection form={form} updateField={updateField} />,
    seo: <SeoSection form={form} updateField={updateField} />,
  };

  return (
    <div className="cof-layout">
      {/* Header */}
      <header className="cof-header">
        <div className="cof-header__left">
          <nav className="ccf-breadcrumb">
            <Link to={paths.viewSection(clientViewId, "courses")}>Courses</Link>
            <span className="ccf-breadcrumb__sep" aria-hidden="true">/</span>
            <span>{isNew ? "New Course" : form.courseName || "Edit"}</span>
          </nav>
          <h1 className="cof-header__title">
            {isNew ? "Add Course" : form.courseName || "Edit Course"}
          </h1>
          {!isNew && form.slug && (
            <p className="cof-header__subtitle">/{form.slug}</p>
          )}
        </div>
        <div className="cof-header__actions">
          <Link to={paths.viewSection(clientViewId, "courses")} className="ccf-btn ccf-btn--ghost">
            Cancel
          </Link>
          <button
            type="button"
            className="ccf-btn ccf-btn--primary"
            disabled={saving}
            onClick={isNew ? handleSubmit : handleSaveDraft}
          >
            {saving ? "Saving..." : isNew ? "Create Course" : "Save Draft"}
          </button>
        </div>
      </header>

      {/* Toast messages */}
      {successMessage && (
        <div className="cof-toast cof-toast--success" role="status">
          <span className="cof-toast__icon" aria-hidden="true">&#10003;</span>
          {successMessage}
          <button className="cof-toast__close" onClick={() => setSuccessMessage("")} aria-label="Dismiss">&times;</button>
        </div>
      )}
      {errorMessage && (
        <div className="cof-toast cof-toast--error" role="alert">
          <span className="cof-toast__icon" aria-hidden="true">!</span>
          {errorMessage}
          <button className="cof-toast__close" onClick={() => setErrorMessage("")} aria-label="Dismiss">&times;</button>
        </div>
      )}

      {/* Tab navigation */}
      <nav className="cof-tabs" aria-label="Form sections">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`cof-tab ${activeTab === tab.key ? "cof-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
            aria-selected={activeTab === tab.key}
          >
            <span className="cof-tab__icon" aria-hidden="true">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Two-column body */}
      <div className="cof-body">
        {/* Left: Active tab content */}
        <form onSubmit={handleSubmit} className="cof-main" id="course-form">
          {tabContent[activeTab]}
        </form>

        {/* Right: Sidebar */}
        <aside className="ccf-sidebar">
          {/* Status & Visibility */}
          <section className="ccf-card ccf-card--sidebar">
            <h2 className="ccf-card__title">Status & Visibility</h2>
            <div className="ccf-card__body">
              <FormField label="Display status">
                <select value={form.displayStatus} onChange={updateField("displayStatus")} className="ccf-input ccf-input--select">
                  {DISPLAY_STATUSES.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </FormField>
              <label className="ccf-toggle">
                <input type="checkbox" checked={form.isFeatured} onChange={updateField("isFeatured")} />
                <span className="ccf-toggle__track" />
                <span className="ccf-toggle__label">
                  Featured course
                  <span className="ccf-toggle__sublabel">Show prominently on the site</span>
                </span>
              </label>
              <FormField label="Sort order" hint="Lower numbers appear first">
                <input type="number" value={form.sortOrder} onChange={updateField("sortOrder")} className="ccf-input" placeholder="0" />
              </FormField>
            </div>
          </section>

          {/* Publishing */}
          {!isNew && publishing && (
            <section className="ccf-card ccf-card--sidebar">
              <div className="ccf-card__title-row">
                <h2 className="ccf-card__title">Publishing</h2>
                <StatusBadge
                  tone={STATUS_TONE[publishing.publishStatus] || "neutral"}
                  label={publishing.publishStatusLabel || publishing.publishStatus}
                />
              </div>
              <div className="ccf-card__body">
                {publishHint && <p className="ccf-publish-hint">{publishHint}</p>}

                {publishing.rejectionComments && (
                  <div className="ccf-rejection">
                    <strong>Review feedback</strong>
                    <p>{publishing.rejectionComments}</p>
                  </div>
                )}

                <dl className="ccf-publish-meta">
                  {publishing.lastSubmittedAt && (
                    <div><dt>Submitted</dt><dd>{formatDateTime(publishing.lastSubmittedAt)}</dd></div>
                  )}
                  {publishing.lastReviewedAt && (
                    <div><dt>Reviewed</dt><dd>{formatDateTime(publishing.lastReviewedAt)}</dd></div>
                  )}
                  {publishing.publishedAt && (
                    <div><dt>Published</dt><dd>{formatDateTime(publishing.publishedAt)}</dd></div>
                  )}
                </dl>

                {canManage && (
                  <div className="ccf-publish-actions">
                    <button
                      type="button"
                      className="ccf-btn ccf-btn--primary ccf-btn--full"
                      disabled={saving || publishing.hasPendingReview}
                      onClick={handleSaveDraft}
                    >
                      {saving ? "Saving..." : "Save Draft"}
                    </button>
                    <button
                      type="button"
                      className="ccf-btn ccf-btn--outline ccf-btn--full"
                      disabled={saving || !canSubmitReview}
                      onClick={handleSubmitReview}
                    >
                      Submit for Review
                    </button>
                    <Link
                      to={paths.viewSection(clientViewId, "courses", courseId, "preview")}
                      className="ccf-btn ccf-btn--ghost ccf-btn--full"
                    >
                      Preview Draft
                    </Link>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Version History */}
          {!isNew && versions.length > 0 && (
            <section className="ccf-card ccf-card--sidebar">
              <h2 className="ccf-card__title">Version History</h2>
              <div className="ccf-card__body">
                <ul className="ccf-versions">
                  {versions.map((v) => (
                    <li key={v.contentVersionId} className="ccf-versions__item">
                      <span className="ccf-versions__num">v{v.versionNumber}</span>
                      <div className="ccf-versions__detail">
                        <span className="ccf-versions__status">{v.publishStatusLabel}</span>
                        {v.submittedAt && <span className="ccf-versions__date">Submitted {formatDateTime(v.submittedAt)}</span>}
                        {v.publishedAt && <span className="ccf-versions__date">Published {formatDateTime(v.publishedAt)}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Danger Zone */}
          {!isNew && canManage && (
            <section className="ccf-card ccf-card--sidebar ccf-card--danger">
              <h2 className="ccf-card__title">Danger Zone</h2>
              <div className="ccf-card__body">
                <p className="ccf-danger-text">Archiving removes this course from public view. This action can be reversed by an admin.</p>
                <button
                  type="button"
                  className="ccf-btn ccf-btn--danger ccf-btn--full"
                  disabled={saving}
                  onClick={handleArchive}
                >
                  Archive Course
                </button>
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ClientCourseFormPage;
